import { z } from 'zod';
/**
 * Dealer onboarding contract (Phase 14 — B2B portal).
 *
 * A dealer APPLICATION is the manual-review record a prospective B2B partner
 * submits (company, legal id, contacts). An admin reviews the attached KYB
 * documents through the EXISTING documents/KYC review flow (admin-documents
 * module — no live Sumsub KYB) and then approves or rejects the application.
 *
 * On approval the platform grants the `dealer` ROLE. Whether it also touches the
 * pricing plan tier depends on the active rule set (see pricing-rules.ts
 * `PricingPlanTier`): with volume tiers published (`tierRules`) the tier is
 * earned from paid cars and approval leaves it unchanged; with `tierRules: null`
 * the legacy behaviour stands and approval sets the tier to `dealer`. No money
 * moves on approval.
 *
 * State machine (mirrors the KYC ladder style): `pending → approved | rejected`,
 * with a single re-application escape hatch `rejected → pending`. `approved` is
 * terminal.
 */
/** Ukrainian legal forms a dealer can register under. */
export const DealerLegalFormSchema = z.enum([
    'tov', // ТОВ — limited liability company (ЄДРПОУ, 8 digits)
    'fop', // ФОП — sole proprietor (РНОКПП/ІПН, 10 digits)
]);
export const DealerApplicationStateSchema = z.enum(['pending', 'approved', 'rejected']);
export const DEALER_APPLICATION_STATES = DealerApplicationStateSchema.options;
/** The default state of an application document written without an explicit state. */
export const DEFAULT_DEALER_APPLICATION_STATE = 'pending';
/** Normalize an unknown/absent stored value to a DealerApplicationState. */
export function effectiveDealerState(value) {
    const parsed = DealerApplicationStateSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_DEALER_APPLICATION_STATE;
}
/**
 * Allowed transitions. `pending` → approved|rejected; a `rejected` applicant may
 * re-apply (`rejected → pending`); `approved` is terminal.
 */
export const DEALER_APPLICATION_TRANSITIONS = {
    pending: ['approved', 'rejected'],
    rejected: ['pending'],
    approved: [],
};
/**
 * Pure transition guard. A no-op (`from === to`) is rejected; a `rejected`
 * transition (admin declining an application) MUST carry a non-empty reason.
 */
export function canDealerTransition(from, to, options = {}) {
    const fromAllowed = DEALER_APPLICATION_TRANSITIONS[from];
    const toExists = DEALER_APPLICATION_TRANSITIONS[to];
    if (fromAllowed === undefined || toExists === undefined) {
        return { ok: false, code: 'UNKNOWN_STATE' };
    }
    if (from === to) {
        return { ok: false, code: 'SAME_STATE' };
    }
    if (fromAllowed.length === 0) {
        return { ok: false, code: 'TERMINAL' };
    }
    if (!fromAllowed.includes(to)) {
        return { ok: false, code: 'INVALID_TRANSITION' };
    }
    if (to === 'rejected' && (!options.reason || options.reason.trim() === '')) {
        return { ok: false, code: 'REASON_REQUIRED' };
    }
    return { ok: true };
}
/** A tax id is 8 digits (ЄДРПОУ, ТОВ) or 10 digits (РНОКПП, ФОП). */
export const DEALER_TAX_ID_REGEX = /^\d{8}$|^\d{10}$/;
/** Public application payload (POST /api/dealer/apply). */
export const DealerApplicationInputSchema = z.object({
    companyName: z.string().min(2).max(200),
    legalForm: DealerLegalFormSchema,
    taxId: z.string().regex(DEALER_TAX_ID_REGEX, 'taxId must be 8 (ЄДРПОУ) or 10 (РНОКПП) digits'),
    contactName: z.string().min(2).max(120),
    contactEmail: z.string().email(),
    contactPhone: z.string().min(5).max(32),
    website: z.string().url().nullish(),
    message: z.string().max(2000).nullish(),
});
/** Persisted shape (`dealer_applications` collection). */
export const DealerApplicationSchema = DealerApplicationInputSchema.extend({
    _id: z.unknown().nullish(),
    userId: z.string(),
    state: DealerApplicationStateSchema.default('pending'),
    /** Admin userId who last reviewed the application. */
    reviewedBy: z.string().nullish(),
    /** Mandatory when the application is rejected. */
    reviewReason: z.string().nullish(),
    createdAt: z.coerce.date().nullish(),
    updatedAt: z.coerce.date().nullish(),
});
//# sourceMappingURL=dealer.js.map